import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import introBackground from '@volumental/assets/images/scanner/loginPage.png';
import {
  Button,
  Container,
  Content,
  ErrorText,
  Headline,
  isValidEmail,
  TextButton,
} from '@volumental/common';
import { type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendOTP } from '../../auth';
import { BrandingFooter, Input } from '../../components/accessible-components';
import Intro from '../../components/intro';
import { useInputStore, useSessionStore } from '../../store';

interface SignInProps {
  changeFlow: () => void;
  onValidate: () => void;
  onClose: () => void;
}

export function SignIn({
  changeFlow,
  onValidate,
  onClose,
}: SignInProps): JSX.Element {
  const { t } = useTranslation();
  const { context } = useSessionStore((store) => store);
  const { email, setEmail } = useInputStore((store) => store);

  const [value, setValue] = useState(email ?? '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [emailError, setEmailError] = useState<boolean>(false);

  useEffect(() => {
    usageTracker.track(UsageTrackerEvent.SignInStart);
  }, []);

  const handleEmailChange = (event: string): void => {
    if (event.length === 0) {
      setEmailError(false);
    } else {
      setEmailError(!isValidEmail(event.toLowerCase()));
    }
    setValue(event.toLowerCase());
  };

  const submitEmail = (): void => {
    setEmailError(!isValidEmail(value));
    if (value && isValidEmail(value)) {
      setErrorMessage('');
      sendOTP(value)
        .then(() => {
          usageTracker.track(UsageTrackerEvent.InputEmail);
          setEmail(value);
          onValidate();
        })
        .catch(() => {
          setErrorMessage(t('somethingWentWrongFinding'));
        });
    } else {
      setErrorMessage(t('pleaseEnterAValidEmail'));
    }
  };

  return (
    <Container>
      <Intro
        imgSrc={introBackground}
        close={onClose}
        imgAlt={t('imageAlt.intro')}
      >
        <Content>
          <Headline style={{ textAlign: 'center', margin: '2rem 0' }}>
            {t('scannedBefore')}
          </Headline>
          <div
            style={{
              paddingBottom: '24px',
            }}
          >
            <Input
              value={value}
              onChange={handleEmailChange}
              error={emailError}
              type="email"
              crossClick={() => {
                setValue('');
                setEmailError(false);
              }}
              placeholder={t('emailPlaceholder')}
              applyBackground
              ariaLabel={t('ariaLabels.emailInput')}
            />
            {Boolean(errorMessage) && <ErrorText>{errorMessage}</ErrorText>}
          </div>
          <Button loading={false} type="submit" onClick={submitEmail}>
            {t('button.submit')}
          </Button>
          <TextButton onClick={changeFlow}>{t('dontHaveAFootScan')}</TextButton>
        </Content>
        <BrandingFooter limitedBranding={context === 'shopify'} />
      </Intro>
    </Container>
  );
}
